import React, { useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountBalance as FinanceIcon,
  ArrowForward as ArrowForwardIcon,
  Assignment as ComplianceIcon,
  AutoGraph as IntelligenceIcon,
  Bolt as EnergyIcon,
  Business as ScaleIcon,
  CloudUpload as DocumentsIcon,
  Dashboard as DashboardIcon,
  ExpandMore as ExpandMoreIcon,
  GppGood as PatentIcon,
  Hub as MarketIcon,
  Insights as InsightsIcon,
  Login as LoginIcon,
  PersonAdd as RegisterIcon,
  MailOutline as ContactIcon,
  Nature as EcoIcon,
  Factory as FactoryIcon,
  Public as ClimateIcon,
  Savings as SavingsIcon,
  Speed as TransitionIcon,
  TrackChanges as MeasureIcon,
  Forest as OffsetsIcon,
  CurrencyRupee as PricingIcon,
  SyncAlt as ConnectIcon,
  Assessment as AssessIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { useRegistration } from '../context/RegistrationContext';
import BrandLockup from './BrandLockup';
import { brandPreloginSceneSrc, BRAND_PATENT_PENDING_BANNER } from '../constants/brand';
import {
  CARBON_OFFSET_TEASER_SECTION,
  CLIMATE_AND_SAVINGS_HIGHLIGHTS,
  COMPLIANCE_REPORTING_FRAMEWORKS,
  COMPLIANCE_REPORTING_SECTION,
  INDIA_SPECIFIC_INFO,
  PATENT_PENDING_HIGHLIGHT,
  PLATFORM_BENEFITS,
  POST_LOGIN_WORKFLOW,
  PRE_LOGIN_FAQ,
  PRE_LOGIN_FOOTER_CTA,
  PRE_LOGIN_HERO,
  PRE_LOGIN_PILLARS,
  PRE_LOGIN_VALUE_PROPOSITION,
  PRICING_TEASER_SECTION,
  PreLoginHighlight,
  PreLoginPillar,
  PreLoginComplianceFramework,
  PreLoginHighlightIcon,
  PreLoginWorkflowIcon,
  WHY_MEASUREMENT_MATTERS,
} from '../constants/preLoginDashboardContent';
import { scrollToPageSection } from '../utils/scrollToPageSection';
import { preLoginHorizontalBannerImageSx } from '../constants/bannerIllustrationLayout';
import { resolveAuthenticatedHomePath } from '../utils/resolvePostLoginDestination';
import RouteLoadingGate from './RouteLoadingGate';
import MarketingHeading from './shared/MarketingHeading';
import ProductVideosSection from './marketing/ProductVideosSection';
import ArticlesTeaserSection from './marketing/ArticlesTeaserSection';

const SECTION_ICONS = {
  why: MeasureIcon,
  climate: ClimateIcon,
  benefits: InsightsIcon,
  india: FactoryIcon,
} as const;

const PILLAR_ICONS = [IntelligenceIcon, InsightsIcon, TransitionIcon] as const;

const HIGHLIGHT_ICONS: Record<PreLoginHighlightIcon, React.ElementType> = {
  climate: ClimateIcon,
  finance: FinanceIcon,
  reduction: MeasureIcon,
  compliance: ComplianceIcon,
  scope: EcoIcon,
  savings: SavingsIcon,
  insights: IntelligenceIcon,
  offsets: OffsetsIcon,
  documents: DocumentsIcon,
  dashboard: DashboardIcon,
  scale: ScaleIcon,
  energy: EnergyIcon,
  market: MarketIcon,
  msme: FactoryIcon,
};

const WORKFLOW_ICONS: Record<PreLoginWorkflowIcon, React.ElementType> = {
  register: RegisterIcon,
  connect: ConnectIcon,
  assess: AssessIcon,
  report: ComplianceIcon,
};

const preLoginBannerImageSx = {
  display: 'block',
  width: '100%',
  maxWidth: '100%',
  height: 'auto',
  objectFit: 'contain' as const,
  objectPosition: 'center' as const,
  bgcolor: 'action.hover',
  p: { xs: 1.5, md: 2 },
};

interface SectionBandProps {
  id?: string;
  children: React.ReactNode;
  variant?: 'default' | 'elevated';
  sx?: object;
}

const SectionBand: React.FC<SectionBandProps> = ({ id, children, variant = 'default', sx }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      id={id}
      component="section"
      sx={{
        py: { xs: 4, md: 5 },
        px: { xs: 0, md: 1 },
        mx: { xs: -0.5, md: 0 },
        borderRadius: { xs: 0, md: 3 },
        bgcolor:
          variant === 'elevated'
            ? isDark
              ? alpha(theme.palette.primary.main, 0.06)
              : alpha(theme.palette.primary.main, 0.03)
            : 'transparent',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

interface SectionIntroProps {
  title: string;
  description?: string;
  align?: 'left' | 'center';
}

const SectionIntro: React.FC<SectionIntroProps> = ({ title, description, align = 'left' }) => (
  <Box sx={{ mb: { xs: 2.5, md: 3.5 }, textAlign: align, maxWidth: align === 'center' ? 820 : undefined, mx: align === 'center' ? 'auto' : undefined }}>
    <MarketingHeading level="h2" sx={{ mb: description ? 1.5 : 0 }}>
      {title}
    </MarketingHeading>
    {description ? (
      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: { xs: '1rem', md: '1.0625rem' } }}>
        {description}
      </Typography>
    ) : null}
  </Box>
);

interface HighlightSectionProps {
  id: string;
  title: string;
  description?: string;
  items: PreLoginHighlight[];
  icon: React.ElementType;
}

const HighlightSection: React.FC<HighlightSectionProps> = ({ id, title, description, items, icon: SectionIcon }) => {
  const theme = useTheme();

  return (
    <SectionBand id={id}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: description ? 1 : 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          <SectionIcon />
        </Box>
        <MarketingHeading level="h3">
          {title}
        </MarketingHeading>
      </Stack>
      {description ? (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720, lineHeight: 1.7 }}>
          {description}
        </Typography>
      ) : null}
      <Grid container spacing={2.5}>
        {items.map((item) => {
          const ItemIcon = HIGHLIGHT_ICONS[item.icon] ?? InsightsIcon;
          return (
          <Grid item xs={12} sm={6} md={3} key={item.title}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                borderColor: alpha(theme.palette.divider, 0.9),
                transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  boxShadow: theme.shadows[3],
                  transform: 'translateY(-3px)',
                },
              }}
            >
              <CardContent sx={{ py: 2.5, px: 2.25, '&:last-child': { pb: 2.5 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 52,
                    height: 52,
                    mb: 1.75,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                >
                  <ItemIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75, letterSpacing: '-0.01em' }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  {item.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          );
        })}
      </Grid>
    </SectionBand>
  );
};

interface ComplianceFrameworkCardProps {
  framework: PreLoginComplianceFramework;
}

const ComplianceFrameworkCard: React.FC<ComplianceFrameworkCardProps> = ({ framework }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        p: 2,
        borderRadius: 2.5,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.35),
          boxShadow: theme.shadows[2],
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <Chip
          label={framework.name}
          size="small"
          color={framework.track === 'india' ? 'primary' : 'secondary'}
          sx={{ fontWeight: 700 }}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
        {framework.subtitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
        {framework.description}
      </Typography>
    </Paper>
  );
};

const ComplianceReportingSection: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const indiaFrameworks = COMPLIANCE_REPORTING_FRAMEWORKS.filter((framework) => framework.track === 'india');
  const exportFrameworks = COMPLIANCE_REPORTING_FRAMEWORKS.filter((framework) => framework.track === 'export');

  return (
    <SectionBand
      id="compliance-reporting"
      variant="elevated"
      sx={{ px: { xs: 2, md: 3 }, mx: { xs: -1, md: 0 }, borderRadius: { xs: 2, md: 3 } }}
    >
      <Grid container spacing={3} alignItems="center" sx={{ mb: { xs: 3, md: 4 } }}>
        <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={COMPLIANCE_REPORTING_SECTION.image}
              alt={COMPLIANCE_REPORTING_SECTION.imageAlt}
              sx={{
                ...preLoginHorizontalBannerImageSx,
                ...preLoginBannerImageSx,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                boxShadow: isDark ? theme.shadows[8] : '0 20px 50px rgba(15, 23, 42, 0.12)',
              }}
            />
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            >
              <ComplianceIcon />
            </Box>
            <MarketingHeading level="h3">{COMPLIANCE_REPORTING_SECTION.title}</MarketingHeading>
          </Stack>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            {COMPLIANCE_REPORTING_SECTION.description}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
            {['BRSR', 'ISO 14064', 'ISO 14067', 'CBAM', 'CSRD', 'CDP', 'TCFD'].map((label) => (
              <Chip key={label} label={label} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
            ))}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              component={RouterLink}
              to="/register?type=msme"
              variant="contained"
              startIcon={<RegisterIcon />}
              sx={{
                background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
                },
              }}
            >
              {COMPLIANCE_REPORTING_SECTION.ctaRegister}
            </Button>
            <Button component={RouterLink} to="/login" variant="outlined" startIcon={<LoginIcon />}>
              {COMPLIANCE_REPORTING_SECTION.ctaLogin}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Typography variant="overline" color="primary" sx={{ fontWeight: 800, letterSpacing: 1.1, display: 'block', mb: 1.5 }}>
        {COMPLIANCE_REPORTING_SECTION.indiaHeading}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {indiaFrameworks.map((framework) => (
          <Grid item xs={12} sm={6} md={3} key={framework.name}>
            <ComplianceFrameworkCard framework={framework} />
          </Grid>
        ))}
      </Grid>

      <Typography variant="overline" color="secondary" sx={{ fontWeight: 800, letterSpacing: 1.1, display: 'block', mb: 1.5 }}>
        {COMPLIANCE_REPORTING_SECTION.exportHeading}
      </Typography>
      <Grid container spacing={2}>
        {exportFrameworks.map((framework) => (
          <Grid item xs={12} sm={6} md={3} key={framework.name}>
            <ComplianceFrameworkCard framework={framework} />
          </Grid>
        ))}
      </Grid>
    </SectionBand>
  );
};

interface PillarCardProps {
  pillar: PreLoginPillar;
  icon: React.ElementType;
}

const PillarCard: React.FC<PillarCardProps> = ({ pillar, icon: Icon }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        p: { xs: 2.5, md: 3 },
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          boxShadow: theme.shadows[2],
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: 2,
          mb: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
        }}
      >
        <Icon />
      </Box>
      <MarketingHeading level="h3" sx={{ mb: 1 }}>
        {pillar.title}
      </MarketingHeading>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
        {pillar.description}
      </Typography>
    </Paper>
  );
};

const PreLoginDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isRegistered, isLoggingOut } = useRegistration();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    if (isRegistered && !isLoggingOut) {
      navigate(resolveAuthenticatedHomePath(), { replace: true });
    }
  }, [isRegistered, isLoggingOut, navigate]);

  if (isLoggingOut) {
    return <RouteLoadingGate message="Signing out…" minHeight="50vh" />;
  }

  if (isRegistered) {
    return null;
  }

  const heroPaperSx = {
    position: 'relative' as const,
    overflow: 'hidden',
    borderRadius: { xs: 2, md: 4 },
    border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.14)}`,
    background: isDark
      ? `linear-gradient(160deg, ${alpha(theme.palette.primary.dark, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.92)} 55%, ${alpha(theme.palette.background.default, 0.95)} 100%)`
      : `linear-gradient(160deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 45%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -120,
      right: -80,
      width: 320,
      height: 320,
      borderRadius: '50%',
      background: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08),
      pointerEvents: 'none',
    },
  };

  return (
    <Box sx={{ pb: 2 }}>
      <Paper elevation={0} sx={{ ...heroPaperSx, p: { xs: 2.5, sm: 3, md: 4 }, mb: { xs: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
          <BrandLockup
            variant="full"
            logoSrc={brandPreloginSceneSrc(isDark ? 'dark' : 'light')}
            logoWidth="min(920px, 96vw)"
          />
          <Box
            sx={{
              mt: 2.5,
              mx: 'auto',
              maxWidth: 720,
              px: { xs: 2, sm: 2.5 },
              py: 1.5,
              borderRadius: 2,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
              bgcolor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <Chip
              icon={<PatentIcon />}
              label={PATENT_PENDING_HIGHLIGHT.label.toUpperCase()}
              color="primary"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.06,
                height: 32,
                '& .MuiChip-label': { fontSize: '0.95rem', px: 1 },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isDark ? 'primary.light' : 'primary.dark',
                lineHeight: 1.45,
                textAlign: 'center',
              }}
            >
              {BRAND_PATENT_PENDING_BANNER}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
            <Chip label={PRE_LOGIN_HERO.chipLabel} size="small" color="primary" variant="outlined" sx={{ mb: 2.5, fontWeight: 600 }} />
            <MarketingHeading level="display" gradient gutterBottom sx={{ mb: 2 }}>
              {PRE_LOGIN_HERO.title}
            </MarketingHeading>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 3, fontWeight: 400, lineHeight: 1.65, fontSize: { xs: '1rem', md: '1.125rem' }, maxWidth: 560 }}
            >
              {PRE_LOGIN_HERO.subtitle}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                component={RouterLink}
                to="/register?type=msme"
                variant="contained"
                size="large"
                startIcon={<RegisterIcon />}
                aria-label={PRE_LOGIN_HERO.registerAriaLabel}
                sx={{
                  py: 1.35,
                  px: 2.5,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
                  },
                }}
              >
                Get started
              </Button>
              <Button
                component="a"
                href={`mailto:${PRE_LOGIN_HERO.contactEmail}`}
                variant="outlined"
                size="large"
                startIcon={<ContactIcon />}
                color="primary"
                sx={{ py: 1.35, px: 2.5, fontWeight: 600 }}
              >
                {PRE_LOGIN_HERO.contactLabel}
              </Button>
              <Button
                component={RouterLink}
                to="/login"
                variant="text"
                size="large"
                startIcon={<LoginIcon />}
                aria-label={PRE_LOGIN_HERO.loginAriaLabel}
                color="primary"
                sx={{ py: 1.35, fontWeight: 600 }}
              >
                {PRE_LOGIN_HERO.loginLabel}
              </Button>
              <Button
                variant="text"
                size="large"
                startIcon={<OffsetsIcon />}
                color="primary"
                onClick={() => scrollToPageSection('carbon-offsets')}
                sx={{ py: 1.35, fontWeight: 600 }}
              >
                Carbon offsets
              </Button>
              <Button
                component={RouterLink}
                to="/pricing"
                variant="text"
                size="large"
                startIcon={<PricingIcon />}
                color="primary"
                sx={{ py: 1.35, fontWeight: 600 }}
              >
                Pricing
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2.5 }}>
              <Chip icon={<EcoIcon />} label="GHG scopes 1–3" size="small" variant="outlined" />
              <Chip icon={<ComplianceIcon />} label="ISO 14067 PCF" size="small" variant="outlined" />
              <Chip icon={<ComplianceIcon />} label="CBAM reporting" size="small" variant="outlined" />
              <Chip icon={<SavingsIcon />} label="Carbon savings" size="small" variant="outlined" />
              <Chip icon={<OffsetsIcon />} label="Carbon offsets" size="small" variant="outlined" />
              <Chip icon={<FinanceIcon />} label="Green finance" size="small" variant="outlined" />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
            <Box
              component="img"
              src={PRE_LOGIN_HERO.image}
              alt={PRE_LOGIN_HERO.imageAlt}
              sx={{
                ...preLoginHorizontalBannerImageSx,
                ...preLoginBannerImageSx,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                boxShadow: isDark ? theme.shadows[8] : '0 20px 50px rgba(15, 23, 42, 0.12)',
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <SectionBand id="product-videos" sx={{ mt: { xs: 0, md: 1 } }}>
        <ProductVideosSection />
      </SectionBand>

      <SectionBand variant="elevated" sx={{ px: { xs: 2, md: 3 }, mx: { xs: -1, md: 0 }, borderRadius: { xs: 2, md: 3 } }}>
        <SectionIntro
          title={PRE_LOGIN_VALUE_PROPOSITION.title}
          description={PRE_LOGIN_VALUE_PROPOSITION.description}
          align="center"
        />
        <Grid container spacing={2.5}>
          {PRE_LOGIN_PILLARS.map((pillar, index) => (
            <Grid item xs={12} md={4} key={pillar.title}>
              <PillarCard pillar={pillar} icon={PILLAR_ICONS[index] ?? IntelligenceIcon} />
            </Grid>
          ))}
        </Grid>
      </SectionBand>

      <Divider sx={{ my: { xs: 1, md: 2 }, opacity: 0.6 }} />

      <HighlightSection
        id="why-measure"
        title="Why carbon measurement matters"
        description="Understand emissions alongside cost and operations to meet buyer, lender, and regulatory expectations."
        items={WHY_MEASUREMENT_MATTERS}
        icon={SECTION_ICONS.why}
      />

      <HighlightSection
        id="climate-savings"
        title="Climate impact & carbon savings"
        description="Track scope-wise visibility, quantify avoided emissions, and connect spend lines to carbon hotspots."
        items={CLIMATE_AND_SAVINGS_HIGHLIGHTS}
        icon={SECTION_ICONS.climate}
      />

      <SectionBand id="carbon-offsets" variant="elevated" sx={{ px: { xs: 2, md: 3 }, mx: { xs: -1, md: 0 }, borderRadius: { xs: 2, md: 3 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={CARBON_OFFSET_TEASER_SECTION.image}
              alt={CARBON_OFFSET_TEASER_SECTION.imageAlt}
              sx={{
                ...preLoginHorizontalBannerImageSx,
                ...preLoginBannerImageSx,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                boxShadow: theme.shadows[3],
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: 'secondary.main',
                }}
              >
                <OffsetsIcon />
              </Box>
              <MarketingHeading level="h3">
                {CARBON_OFFSET_TEASER_SECTION.title}
              </MarketingHeading>
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              {CARBON_OFFSET_TEASER_SECTION.description}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button component={RouterLink} to="/register?type=msme" variant="contained" color="secondary" startIcon={<OffsetsIcon />}>
                {CARBON_OFFSET_TEASER_SECTION.ctaMarketplace}
              </Button>
              <Button component={RouterLink} to="/register?type=msme" variant="outlined" startIcon={<RegisterIcon />}>
                {CARBON_OFFSET_TEASER_SECTION.ctaRegister}
              </Button>
              <Button component={RouterLink} to="/login" variant="text" startIcon={<LoginIcon />} color="primary">
                {CARBON_OFFSET_TEASER_SECTION.ctaLogin}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </SectionBand>

      <SectionBand id="pricing">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={PRICING_TEASER_SECTION.image}
              alt={PRICING_TEASER_SECTION.imageAlt}
              sx={{
                ...preLoginHorizontalBannerImageSx,
                ...preLoginBannerImageSx,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                boxShadow: theme.shadows[3],
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              >
                <PricingIcon />
              </Box>
              <MarketingHeading level="h3">
                {PRICING_TEASER_SECTION.title}
              </MarketingHeading>
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
              {PRICING_TEASER_SECTION.description}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
              {PRICING_TEASER_SECTION.planPriceChips.map((plan) => (
                <Chip
                  key={plan.label}
                  label={`${plan.label}: ${plan.amount}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                component={RouterLink}
                to="/pricing"
                variant="contained"
                startIcon={<PricingIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
                  },
                }}
              >
                {PRICING_TEASER_SECTION.ctaPricing}
              </Button>
              <Button component={RouterLink} to="/register?type=msme" variant="outlined" startIcon={<RegisterIcon />}>
                {PRICING_TEASER_SECTION.ctaRegister}
              </Button>
              <Button component={RouterLink} to="/login" variant="text" startIcon={<LoginIcon />} color="primary">
                {PRICING_TEASER_SECTION.ctaLogin}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </SectionBand>

      <HighlightSection
        id="benefits"
        title="Platform benefits after sign-in"
        description="From document capture to compliance reporting—everything you need in one MSME-sized workspace."
        items={PLATFORM_BENEFITS}
        icon={SECTION_ICONS.benefits}
      />

      <ComplianceReportingSection />

      <HighlightSection
        id="india"
        title="India-specific context"
        description="Built for domestic supply chains, BRSR value-chain expectations, and emerging carbon-market programmes."
        items={INDIA_SPECIFIC_INFO}
        icon={SECTION_ICONS.india}
      />

      <SectionBand id="insights" variant="elevated" sx={{ px: { xs: 2, md: 3 }, mx: { xs: -1, md: 0 }, borderRadius: { xs: 2, md: 3 } }}>
        <ArticlesTeaserSection />
      </SectionBand>

      <SectionBand id="workflow">
        <SectionIntro title="Your workflow after login" description="Four steps from registration to continuous improvement." />
        <Grid container spacing={2.5}>
          {POST_LOGIN_WORKFLOW.map((step) => {
            const StepIcon = WORKFLOW_ICONS[step.icon] ?? RegisterIcon;
            return (
            <Grid item xs={12} sm={6} md={3} key={step.step}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': {
                    boxShadow: theme.shadows[3],
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ p: 2.25, pb: 1.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Chip label={`Step ${step.step}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
                  </Stack>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                    }}
                  >
                    <StepIcon sx={{ fontSize: 30 }} />
                  </Box>
                </Box>
                <Box sx={{ px: 2.25, pb: 2.25 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {step.description}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            );
          })}
        </Grid>
      </SectionBand>

      <SectionBand id="faq" variant="elevated" sx={{ px: { xs: 2, md: 3 }, mx: { xs: -1, md: 0 }, borderRadius: { xs: 2, md: 3 } }}>
        <SectionIntro title="Frequently asked questions" align="center" />
        <Box sx={{ maxWidth: 860, mx: 'auto' }}>
          {PRE_LOGIN_FAQ.map((item) => (
            <Accordion
              key={item.question}
              disableGutters
              elevation={0}
              sx={{
                mb: 1,
                borderRadius: '12px !important',
                border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                '&::before': { display: 'none' },
                bgcolor: 'background.paper',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, py: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </SectionBand>

      <Paper
        elevation={0}
        sx={{
          mt: { xs: 3, md: 4 },
          p: { xs: 2.5, md: 4 },
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.24 : 0.16)}`,
          bgcolor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.04),
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src={PRE_LOGIN_HERO.image}
              alt="Preview of emissions dashboard after sign-in"
              sx={{
                ...preLoginHorizontalBannerImageSx,
                ...preLoginBannerImageSx,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              }}
            />
          </Grid>
          <Grid item xs={12} md={7} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <MarketingHeading level="h2" sx={{ mb: 1 }}>
              {PRE_LOGIN_FOOTER_CTA.title}
            </MarketingHeading>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.65 }}>
              Start measuring at your scale with a workspace built for Indian MSME operating realities.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent={{ xs: 'center', md: 'flex-start' }}
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                component={RouterLink}
                to="/register?type=msme"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
                  },
                }}
              >
                {PRE_LOGIN_FOOTER_CTA.registerLabel}
              </Button>
              <Button component={RouterLink} to="/login" variant="outlined" size="large" startIcon={<LoginIcon />} color="primary">
                {PRE_LOGIN_FOOTER_CTA.loginLabel}
              </Button>
              <Button
                component="a"
                href={`mailto:${PRE_LOGIN_HERO.contactEmail}`}
                variant="text"
                size="large"
                startIcon={<ContactIcon />}
                color="primary"
              >
                {PRE_LOGIN_HERO.contactLabel}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PreLoginDashboard;
